import type { ItemCard } from "@/types/content";
import { capitalizeFirst } from "@/utils/text";

/**
 * The query parameter that narrows a product listing to one device model —
 * `?device=artemis`, which the My Account "Compatible accessories" row links to
 * (GROW-397). The backend writes the model lower-cased; the tags it is matched
 * against are whatever casing an editor typed.
 */
export const DEVICE_FILTER_PARAM = "device";

export interface DeviceFilterResult {
  items: ItemCard[];
  /**
   * The device to name in the "Showing products for …" notice: the matching tag
   * as an editor spelled it ("Artemis"), else the raw parameter. Null when no
   * filter applies.
   */
  deviceLabel: string | null;
}

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * Keeps the items tagged as compatible with `device`, case-insensitively.
 * A missing or blank parameter filters nothing. Items tagged with no devices at
 * all are dropped by an active filter: nothing says they fit.
 */
export function filterByDevice(
  items: ItemCard[],
  device: string | null | undefined
): DeviceFilterResult {
  const raw = device?.trim() ?? "";
  const wanted = normalize(raw);
  if (!wanted) return { items, deviceLabel: null };

  let deviceLabel: string | null = null;
  const filtered = items.filter((item) => {
    const tag = item.compatibleDevices?.find(
      (candidate) => normalize(candidate) === wanted
    );
    if (tag !== undefined && deviceLabel === null) deviceLabel = tag.trim();
    return tag !== undefined;
  });

  // Capitalised for display: tags and the parameter are often typed in lower case.
  return { items: filtered, deviceLabel: capitalizeFirst(deviceLabel ?? raw) };
}
